import React, { useState } from 'react';
import { AnalysisResult } from '../types';
import { Timeline } from './Timeline';
import { CheckSquare, Video, FileText, Share, Download, Copy } from 'lucide-react';
import { jsPDF } from "jspdf";

interface ContentDashboardProps {
  data: AnalysisResult;
}

export const ContentDashboard: React.FC<ContentDashboardProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'action' | 'repurpose'>('summary');
  const [isDownloading, setIsDownloading] = useState(false);

  const summary = data.summary ?? [];
  const actionPlan = data.actionPlan ?? [];
  const contentIdeas = {
    reels: data.contentIdeas?.reels ?? [],
    socialPosts: data.contentIdeas?.socialPosts ?? [],
    blogOutline: data.contentIdeas?.blogOutline ?? '',
  };

  const handleDownloadPDF = () => {
    setIsDownloading(true);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = 20;

    // Helper para adicionar texto com quebra de linha
    const addText = (text: string, fontSize: number, isBold: boolean = false) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      
      const splitText = doc.splitTextToSize(text, pageWidth - (margin * 2));
      
      // Verifica se precisa de nova página
      if (yPos + (splitText.length * fontSize * 0.5) > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(splitText, margin, yPos);
      yPos += (splitText.length * fontSize * 0.45) + 4;
    };

    // Título
    addText("Relatório de Estratégia de Conteúdo - TubeContent AI", 18, true);
    yPos += 5;

    // Resumo
    addText("1. Resumo e Pontos Chave", 14, true);
    summary.forEach(s => {
      addText(`[${s.timestamp}] ${s.point}`, 11);
    });
    yPos += 5;

    // Plano de Ação
    if (data.isEducational && actionPlan.length > 0) {
      addText("2. Plano de Ação", 14, true);
      actionPlan.forEach(action => {
        addText(`- ${action}`, 11);
      });
      yPos += 5;
    }

    // Ideias de Conteúdo
    addText("3. Ideias de Reaproveitamento", 14, true);
    
    addText("Reels / TikToks:", 12, true);
    contentIdeas.reels.forEach((idea, i) => addText(`${i+1}. ${idea}`, 10));
    yPos += 3;

    addText("Posts Sociais (Legendas):", 12, true);
    contentIdeas.socialPosts.forEach((post, i) => addText(`${i+1}. ${post}`, 10));
    yPos += 3;

    addText("Esboço de Blog:", 12, true);
    addText(contentIdeas.blogOutline, 10);

    // Salvar
    doc.save("tubecontent-insights.pdf");
    setIsDownloading(false);
  };

  return (
    <div className="h-full flex flex-col gap-6 w-full">
      
      {/* Dashboard Metrics / Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-[24px] border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Tipo de Conteúdo</span>
            <span className={`w-2 h-2 rounded-full ${data.isEducational ? 'bg-green-500' : 'bg-blue-500'}`}></span>
          </div>
          <h3 className="text-lg font-semibold text-slate-800">
            {data.isEducational ? 'Educacional' : 'Entretenimento'}
          </h3>
        </div>
        <div className="bg-white p-5 rounded-[24px] border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Pontos Chave</span>
             <Copy size={14} className="text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">{summary.length} Extraídos</h3>
        </div>
        <div className="bg-white p-5 rounded-[24px] border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Reaproveitado</span>
             <Share size={14} className="text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">3 Formatos Prontos</h3>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-[32px] border border-slate-100 shadow-lg shadow-slate-200/50 overflow-hidden flex flex-col">
        {/* Tabs */}
        <div className="flex items-center gap-1 p-2 border-b border-slate-50">
          {[
            { id: 'summary', label: 'Resumo', icon: Video },
            { id: 'action', label: 'Plano de Ação', icon: CheckSquare },
            { id: 'repurpose', label: 'Ideias de Conteúdo', icon: FileText },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-slate-50 text-slate-900 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
          <div className="ml-auto flex gap-2 pr-4">
             <button 
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className="p-2 text-slate-400 hover:text-slate-900 transition-colors flex items-center gap-2"
                title="Baixar PDF"
             >
                {isDownloading ? <span className="text-xs">Gerando...</span> : <Download size={18}/>}
             </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          
          {activeTab === 'summary' && (
            <div className="w-full">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Destaques do Vídeo</h2>
              <Timeline points={summary} />
            </div>
          )}

          {activeTab === 'action' && (
            <div className="w-full">
              <h2 className="text-xl font-bold text-slate-900 mb-2">Lista de Ação</h2>
              <p className="text-slate-500 mb-8 text-sm">Passos práticos baseados no conteúdo educacional.</p>
              
              {actionPlan.length > 0 ? (
                <div className="space-y-3">
                  {actionPlan.map((action, idx) => (
                    <label key={idx} className="flex items-start gap-4 p-4 rounded-2xl border border-slate-100 hover:bg-slate-50 hover:border-blue-200 transition-all cursor-pointer group">
                      <div className="relative flex items-center">
                        <input type="checkbox" className="peer w-5 h-5 appearance-none border-2 border-slate-300 rounded-md checked:bg-slate-900 checked:border-slate-900 transition-colors" />
                        <svg className="absolute w-3.5 h-3.5 pointer-events-none hidden peer-checked:block text-white left-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span className="text-slate-700 text-sm font-medium group-hover:text-slate-900 pt-0.5">{action}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                    <p className="text-slate-400">Nenhum plano de ação específico detectado para este tipo de conteúdo.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'repurpose' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
               {/* Reels */}
               <div className="space-y-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-6 bg-pink-500 rounded-full"></span>
                    Reels / TikToks Virais
                  </h3>
                  {contentIdeas.reels.map((reel, i) => (
                    <div key={i} className="bg-gradient-to-br from-pink-50/50 to-white p-5 rounded-2xl border border-pink-100/50 shadow-sm">
                        <div className="text-xs font-bold text-pink-400 mb-2 uppercase tracking-wide">Conceito de Gancho {i+1}</div>
                        <p className="text-slate-700 text-sm italic">"{reel}"</p>
                    </div>
                  ))}
               </div>

               {/* Social */}
               <div className="space-y-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <span className="w-2 h-6 bg-blue-500 rounded-full"></span>
                    LinkedIn / Instagram
                  </h3>
                  {contentIdeas.socialPosts.map((post, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                         <div className="text-xs font-bold text-blue-500 mb-2 uppercase tracking-wide">Ideia de Legenda {i+1}</div>
                        <p className="text-slate-600 text-sm whitespace-pre-wrap">{post}</p>
                    </div>
                  ))}
               </div>

               {/* Blog */}
               <div className="xl:col-span-2 mt-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <span className="w-2 h-6 bg-slate-900 rounded-full"></span>
                    Artigo Detalhado (Esboço)
                  </h3>
                  <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 text-slate-700 text-sm font-mono leading-relaxed whitespace-pre-wrap">
                      {contentIdeas.blogOutline}
                  </div>
               </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};